import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { AlertTriangle, LogOut } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  useImpersonationBanner,
  type ImpersonationBannerErrorHandler,
} from "./useImpersonationBanner";
import { formatRemaining } from "./formatRemaining";

export interface ImpersonationBannerLabels {
  /** Ex: "Sessão de suporte — você está navegando como este usuário." */
  title: string;
  /** Função que recebe o remaining string (ex: "4:32") e monta a frase. */
  renderSubtitle: (args: { reason: string; remaining: string }) => ReactNode;
  /** Label do botão para encerrar a sessão. Ex: "Encerrar" */
  endLabel: string;
  /** Label enquanto o end está sendo executado. Ex: "Encerrando…" */
  endingLabel: string;
  /** Título da tela exibida quando a aba não pôde ser fechada. */
  endedTitle: string;
  /** Texto da tela "sessão de suporte encerrada". */
  endedDescription: string;
}

export interface ImpersonationBannerProps {
  supabase: SupabaseClient;
  labels: ImpersonationBannerLabels;
  /** Classe extra opcional, útil se um app quiser ajustar z-index. */
  className?: string;
  /**
   * Falha de claim/encerramento. Os apps ligam no Sentry com tags
   * `{ feature: 'impersonation', action }` (IR-RX-005/IR-OBS-003).
   */
  onError?: ImpersonationBannerErrorHandler;
}

/**
 * Banner da ABA DE SUPORTE (impersonação). Só aparece na aba aberta pelo
 * admin com `?impersonation=<id>` — nas sessões reais do usuário não renderiza
 * nada. Fixed no topo, amarelo, z-index máximo, até o admin clicar em
 * "Encerrar", o TTL (escolhido no Admin: 5, 15, 30 ou 45 min) vencer ou outro
 * admin encerrar pelo painel.
 * Ao terminar, a aba tenta se fechar; se o navegador não deixar, mostra a
 * tela "sessão de suporte encerrada".
 */
export function ImpersonationBanner({
  supabase,
  labels,
  className,
  onError,
}: ImpersonationBannerProps) {
  const { session, ended, end } = useImpersonationBanner(supabase, { onError });
  const [now, setNow] = useState<number>(() => Date.now());
  const [ending, setEnding] = useState(false);
  // Flag sem re-render: evita double-click durante `end()` sem depender do
  // estado `ending`, que pode tentar ser atualizado após unmount (o
  // componente retorna null quando `session` vira null pós-signOut).
  const endingRef = useRef(false);

  useEffect(() => {
    if (!session) return;
    const tick = globalThis.setInterval(() => setNow(Date.now()), 1000);
    return () => globalThis.clearInterval(tick);
  }, [session]);

  if (ended) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "fixed inset-0 z-[2147483647] flex items-center justify-center",
          "bg-background p-6 text-center",
          className,
        )}
        data-testid="impersonation-ended"
      >
        <div className="max-w-sm space-y-2">
          <LogOut className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-lg font-semibold">{labels.endedTitle}</p>
          <p className="text-sm text-muted-foreground">{labels.endedDescription}</p>
        </div>
      </div>
    );
  }

  if (!session) return null;

  const remaining = formatRemaining(session.expires_at, now);

  async function handleEnd() {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    try {
      await end();
      // Após end() o banner some (session vira null) — evitamos setEnding(false)
      // para não disparar update em componente já desmontado.
    } finally {
      endingRef.current = false;
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed top-0 inset-x-0 z-[2147483647] bg-yellow-400 text-yellow-950",
        "border-b-2 border-yellow-600 shadow-md",
        "px-4 py-2 flex items-center gap-3 text-sm font-medium",
        className,
      )}
      data-testid="impersonation-banner"
    >
      <AlertTriangle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold leading-tight">{labels.title}</p>
        <p className="text-xs leading-tight mt-0.5 truncate">
          {labels.renderSubtitle({ reason: session.reason, remaining })}
        </p>
      </div>
      <button
        type="button"
        onClick={handleEnd}
        disabled={ending}
        className={cn(
          "flex-shrink-0 inline-flex items-center gap-1.5 rounded-md",
          "bg-yellow-950 text-yellow-50 px-3 py-1.5 text-xs font-semibold",
          "hover:bg-yellow-900 focus:outline-none focus:ring-2 focus:ring-yellow-900",
          "disabled:opacity-60 disabled:cursor-not-allowed",
        )}
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
        {ending ? labels.endingLabel : labels.endLabel}
      </button>
    </div>
  );
}
